import {
  type ApprovalRequestId,
  type EnvironmentId,
  type ProviderApprovalDecision,
  type ThreadId,
  type ChatAttachment,
} from "@cmmd-forge/contracts";
import { useCallback, useState } from "react";
import { readEnvironmentApi } from "../../environmentApi";
import { newCommandId } from "~/lib/utils";

export function useProviderResponseControls(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
}): {
  readonly respondingRequestIds: ApprovalRequestId[];
  readonly respondingUserInputRequestIds: ApprovalRequestId[];
  readonly onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<void>;
  readonly onRespondToUserInput: (
    requestId: ApprovalRequestId,
    answers: Record<string, unknown>,
    attachmentsByQuestionId?: Record<string, readonly ChatAttachment[]>,
  ) => Promise<void>;
} {
  const { activeThreadId, environmentId, setThreadError } = input;
  const [respondingRequestIds, setRespondingRequestIds] = useState<ApprovalRequestId[]>([]);
  const [respondingUserInputRequestIds, setRespondingUserInputRequestIds] = useState<
    ApprovalRequestId[]
  >([]);

  const onRespondToApproval = useCallback(
    async (requestId: ApprovalRequestId, decision: ProviderApprovalDecision) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !activeThreadId) return;

      setRespondingRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      await api.orchestration
        .dispatchCommand({
          type: "thread.approval.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          decision,
          createdAt: new Date().toISOString(),
        })
        .catch((err: unknown) => {
          setThreadError(
            activeThreadId,
            err instanceof Error ? err.message : "Failed to submit approval decision.",
          );
        });
      setRespondingRequestIds((existing) => existing.filter((id) => id !== requestId));
    },
    [activeThreadId, environmentId, setThreadError],
  );

  const onRespondToUserInput = useCallback(
    async (
      requestId: ApprovalRequestId,
      answers: Record<string, unknown>,
      attachmentsByQuestionId: Record<string, readonly ChatAttachment[]> = {},
    ) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !activeThreadId) {
        throw new Error("This thread is no longer available for a response.");
      }

      setRespondingUserInputRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      try {
        await api.orchestration.dispatchCommand({
          type: "thread.user-input.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          answers,
          attachmentsByQuestionId,
          createdAt: new Date().toISOString(),
        });
      } catch (error) {
        setThreadError(
          activeThreadId,
          error instanceof Error ? error.message : "Failed to submit user input.",
        );
        throw error;
      } finally {
        setRespondingUserInputRequestIds((existing) => existing.filter((id) => id !== requestId));
      }
    },
    [activeThreadId, environmentId, setThreadError],
  );

  return {
    respondingRequestIds,
    respondingUserInputRequestIds,
    onRespondToApproval,
    onRespondToUserInput,
  };
}
